import { FC, HTMLAttributes, ReactNode, useEffect, useState } from 'react'
import { ElCompanyName, ElOfficeSwitcher } from './office-switcher.styles'
import { OfficeMenu, OfficeChildProps } from './office-switcher.types'

interface OfficeSwitcherProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onChange'> {
  menu: OfficeMenu[]
  children?: (props: OfficeChildProps) => ReactNode
  onChange?: (selectedOffice: OfficeMenu) => void
  selectedOfficeId?: string
}

/**
 * Filters the menu array to find an office by ID.
 * If no match is found, returns the first item in the list.
 *
 * @param menu - Array of available offices.
 * @param officeID - Optional office ID to match.
 * @returns The matched `OfficeMenu` object or the first item as fallback.
 */
const filterMenuByID = (menu: OfficeMenu[], officeID?: string): OfficeMenu => {
  return officeID ? menu.filter((menuItem) => menuItem.id == officeID)[0] : menu[0]
}

/**
 * `OfficeSwitcher` is a flexible UI component for selecting an office from a given list.
 * It renders a custom layout through a render prop and displays the currently selected office name.
 *
 * ### Props:
 * @param menu - An array of available office options.
 * Each office should include:
 * - `id` (string): Unique identifier for the office.
 * - `officeName` (string): Display name of the office.
 *
 * @param children - A render prop function that receives:
 * - `selectedOffice` (OfficeMenu): The currently selected office object.
 * - `menu` (OfficeMenu[]): The full list of office options.
 * - `callback` (Function): A callback function to update the selected office.
 *
 * @param onChange - Optional callback invoked when the selected office changes.
 *
 * @param selectedOfficeId - Optional ID of the office to set as selected on initial render.
 *
 * ### Behavior:
 * 1. Filters the `menu` to set the initial selected office based on `selectedOfficeId`.
 * 2. Exposes a `callback` function to update selection programmatically (e.g., from UI interactions).
 * 3. Optionally calls `onChange` when the selected office is updated.
 * 4. Renders the selected office name below the custom content.
 */
export const OfficeSwitcher: FC<OfficeSwitcherProps> = ({
  menu,
  children,
  onChange,
  selectedOfficeId,
  ...restProps
}) => {
  const [selectedOffice, setSelectedOffice] = useState<OfficeMenu>(filterMenuByID(menu, selectedOfficeId))

  // Update selectedOffice when menu or selectedOfficeId changes
  useEffect(() => {
    const updatedOffice = filterMenuByID(menu, selectedOfficeId)
    setSelectedOffice(updatedOffice)
  }, [menu, selectedOfficeId])

  const onClick = (selectedOfficeMenu: OfficeMenu) => () => {
    setSelectedOffice(selectedOfficeMenu)
    onChange?.(selectedOfficeMenu)
  }

  return (
    <ElOfficeSwitcher {...restProps}>
      {children && children({ selectedOffice, menu, onClick })}
      <ElCompanyName>{selectedOffice?.name}</ElCompanyName>
    </ElOfficeSwitcher>
  )
}
